import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { Loader2, X, Upload, Image, Video, Eye, Package, Clock, Pencil, Trash2, KeyRound, CreditCard, CheckCircle, ExternalLink, Star, Plus, Camera } from 'lucide-react';

/* ─── small inline SVGs ─── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4l-6 6 6 6" />
  </svg>
);
const IconUpload = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M10 13V4M6 7l4-4 4 4"/><path d="M3 15v1a2 2 0 002 2h10a2 2 0 002-2v-1"/>
  </svg>
);
const IconFolder = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }}>
    <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/>
  </svg>
);
const IconEye = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="10" cy="10" r="8"/><path d="M6 10h4l2-4" strokeLinecap="round"/>
  </svg>
);

/* ─── reusable style helpers ─── */
const ghostBtn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#e4e1db] text-[#5a5a5a] text-[12px] font-medium hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-colors cursor-pointer";
const inkBtn   = "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0e0e0e] text-white text-[12px] font-medium hover:bg-[#222] transition-colors disabled:opacity-50 cursor-pointer";
const inputCls = "w-full text-[14px] text-[#0e0e0e] border-[#e4e1db] placeholder:text-[#c0bdb8] focus-visible:ring-0 focus-visible:border-[#0e0e0e] focus-visible:shadow-[0_0_0_3px_rgba(14,14,14,0.06)]";

// Static, shared portfolio tag list. Photographers can also create their own
// tags per photo (below); those live on their profile only and never join this list.
const GLOBAL_PORTFOLIO_TAGS = [
  'Black-and-white', 'Fashion', 'Food', 'Landscape', 'Night', 'Portrait',
  'Real Estate', 'Sports', 'Street', 'Travel', 'Wildlife',
].sort((a, b) => a.localeCompare(b));

const EditProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [isPortfolioDragging, setIsPortfolioDragging] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [specialtiesOptions, setSpecialtiesOptions] = useState({ photography: [], videography: [] });
  const [profileId, setProfileId] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profile, setProfile] = useState({
    title: '',
    bio: '',
    specialties: [],
    location: '',
    city: '',
    hourly_rate: 0,
    pricing_type: 'hourly',
    project_price_min: '',
    project_price_max: '',
    travel_locations: [],
    availability: []
  });
  const [customSpecialty, setCustomSpecialty] = useState('');
  const [newPortfolioItem, setNewPortfolioItem] = useState({
    url: '',
    type: 'image',
    title: '',
    description: ''
  });
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [tagMenuOpenId, setTagMenuOpenId] = useState(null);
  const [newTagInput, setNewTagInput] = useState('');

  // Travel location state
  const [newTravelLocation, setNewTravelLocation] = useState({
    location: '',
    city: '',
    start_date: '',
    end_date: ''
  });

  // Stripe Connect state (creatives)
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    onboarding_complete: false,
    payouts_enabled: false
  });
  const [connectingStripe, setConnectingStripe] = useState(false);

  // Billing state (businesses)
  const [billingStatus, setBillingStatus] = useState({ has_payment_method: false, card_last4: null, card_brand: null });
  const [settingUpBilling, setSettingUpBilling] = useState(false);

  // Service packages state
  const [servicePackages, setServicePackages] = useState([]);
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    price: '',
    deliverables: '',
    duration_hours: '',
    revisions_included: 2
  });

  // Password reset state
  const [requestingReset, setRequestingReset] = useState(false);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const config = {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  useEffect(() => {
    fetchProfile();
    fetchSpecialties();
    fetchServicePackages();
    if (user?.role?.toLowerCase() === 'creative') fetchStripeStatus();
    if (user?.role?.toLowerCase() === 'business') fetchBillingStatus();
  }, [user?.role]);

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/specialties`);
      setSpecialtiesOptions(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/connect/status`, config);
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  };

  const fetchBillingStatus = async () => {
    try {
      const response = await axios.get(`${API}/billing/status`, config);
      setBillingStatus(response.data);
    } catch (error) {
      console.error('Error fetching billing status:', error);
    }
  };

  const handleSetupBilling = async () => {
    setSettingUpBilling(true);
    try {
      const response = await axios.post(`${API}/billing/setup`, {}, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up billing');
      setSettingUpBilling(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(`${API}/billing/portal`, {}, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  // Handle return from Stripe onboarding / billing setup
  useEffect(() => {
    if (searchParams.get('billing_setup') === 'success') {
      fetchBillingStatus();
      toast.success('Payment method saved!');
      navigate('/edit-profile', { replace: true });
    }
    if (searchParams.get('stripe_return') === 'true') {
      fetchStripeStatus();
      toast.success('Stripe setup updated!');
      navigate('/edit-profile', { replace: true });
    }
    if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete Stripe onboarding');
      navigate('/edit-profile', { replace: true });
    }
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/creatives/user/${user.user_id}`, config);
      setProfileId(response.data.profile_id);
      setProfilePicture(response.data.user_picture || null);
      setBannerImage(response.data.banner_image || null);
      setProfile({
        title: response.data.title || '',
        bio: response.data.bio || '',
        specialties: response.data.specialties || [],
        location: response.data.location || '',
        city: response.data.city || '',
        hourly_rate: response.data.hourly_rate || 0,
        pricing_type: response.data.pricing_type || 'hourly',
        project_price_min: response.data.project_price_min || '',
        project_price_max: response.data.project_price_max || '',
        travel_locations: response.data.travel_locations || [],
        availability: response.data.availability || []
      });
      setPortfolioItems(response.data.portfolio_items || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServicePackages = async () => {
    try {
      const response = await axios.get(`${API}/service-packages/my`, config);
      setServicePackages(response.data);
    } catch (error) {
      console.error('Error fetching service packages:', error);
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.name || !newPackage.price || !newPackage.deliverables) {
      toast.error('Please fill in name, price, and deliverables');
      return;
    }
    try {
      await axios.post(`${API}/service-packages`, {
        ...newPackage,
        price: parseFloat(newPackage.price),
        duration_hours: parseFloat(newPackage.duration_hours) || 2,
        revisions_included: parseInt(newPackage.revisions_included) || 2
      }, config);
      toast.success('Service package created!');
      setShowPackageForm(false);
      setNewPackage({ name: '', description: '', price: '', deliverables: '', duration_hours: '', revisions_included: 2 });
      fetchServicePackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create package');
    }
  };

  const handleUpdatePackage = async () => {
    if (!editingPackage) return;
    try {
      await axios.put(`${API}/service-packages/${editingPackage.package_id}`, {
        name: editingPackage.name,
        description: editingPackage.description,
        price: parseFloat(editingPackage.price),
        deliverables: editingPackage.deliverables,
        duration_hours: parseFloat(editingPackage.duration_hours) || 2,
        revisions_included: parseInt(editingPackage.revisions_included) || 2
      }, config);
      toast.success('Package updated!');
      setEditingPackage(null);
      fetchServicePackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!confirm('Are you sure you want to delete this package?')) return;
    try {
      await axios.delete(`${API}/service-packages/${packageId}`, config);
      toast.success('Package deleted');
      fetchServicePackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const handleRequestPasswordReset = async () => {
    setRequestingReset(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, {}, config);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setRequestingReset(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      await axios.post(`${API}/connect/create-account`, {}, config);
      const response = await axios.post(`${API}/connect/onboarding-link`, {
        origin_url: window.location.origin
      }, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      setConnectingStripe(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const response = await axios.post(`${API}/connect/dashboard-link`, {}, config);
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open Stripe dashboard');
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/profile-picture`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      setProfilePicture(response.data.url);
      toast.success('Profile picture updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/banner`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      setBannerImage(response.data.url);
      toast.success('Banner image updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handlePortfolioDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPortfolioDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handlePortfolioUploadFile(file);
  };

  const handlePortfolioDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPortfolioDragging(true);
  };

  const handlePortfolioDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPortfolioDragging(false);
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handlePortfolioUploadFile(file);
  };

  const handlePortfolioUploadFile = async (file) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) { toast.error('Please select an image or video file'); return; }
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) { toast.error(`File must be less than ${isVideo ? '100MB' : '10MB'}`); return; }

    setUploadingPortfolio(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('description', '');
      const response = await axios.post(`${API}/upload/portfolio`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      setPortfolioItems(prev => [...prev, response.data.item]);
      toast.success('Portfolio item uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const handleSave = async () => {
    if (!profile.title || !profile.city) {
      toast.error('Please fill in required fields (Title, City)');
      return;
    }
    if (profile.pricing_type === 'hourly' || profile.pricing_type === 'both') {
      if (profile.hourly_rate <= 0) { toast.error('Please enter a valid hourly rate'); return; }
    }
    if (profile.pricing_type === 'project' || profile.pricing_type === 'both') {
      if (!profile.project_price_min || !profile.project_price_max) { toast.error('Please enter project price range'); return; }
    }

    setSaving(true);
    try {
      const cleanProfile = { ...profile };
      if (cleanProfile.project_price_min === '' || cleanProfile.project_price_min === null) delete cleanProfile.project_price_min;
      if (cleanProfile.project_price_max === '' || cleanProfile.project_price_max === null) delete cleanProfile.project_price_max;
      if (cleanProfile.hourly_rate === '' || cleanProfile.hourly_rate === null) delete cleanProfile.hourly_rate;
      await axios.put(`${API}/creatives/profile`, cleanProfile, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSpecialtyToggle = (specialty) => {
    setProfile(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleAddCustomSpecialty = () => {
    if (!customSpecialty.trim()) { toast.error('Please enter a specialty'); return; }
    const trimmed = customSpecialty.trim();
    if (profile.specialties.includes(trimmed)) { toast.error('This specialty is already added'); return; }
    setProfile(prev => ({ ...prev, specialties: [...prev.specialties, trimmed] }));
    setCustomSpecialty('');
    toast.success('Custom specialty added');
  };

  const handleDayToggle = (day) => {
    setProfile(prev => ({
      ...prev,
      availability: prev.availability.includes(day)
        ? prev.availability.filter(d => d !== day)
        : [...prev.availability, day]
    }));
  };

  const addTravelLocation = () => {
    if (!newTravelLocation.location || !newTravelLocation.city || !newTravelLocation.start_date || !newTravelLocation.end_date) {
      toast.error('Please fill in all travel location fields'); return;
    }
    if (new Date(newTravelLocation.end_date) < new Date(newTravelLocation.start_date)) {
      toast.error('End date must be after start date'); return;
    }
    setProfile(prev => ({ ...prev, travel_locations: [...prev.travel_locations, { ...newTravelLocation }] }));
    setNewTravelLocation({ location: '', city: '', start_date: '', end_date: '' });
    toast.success('Travel location added - remember to save your profile!');
  };

  const removeTravelLocation = (index) => {
    setProfile(prev => ({ ...prev, travel_locations: prev.travel_locations.filter((_, i) => i !== index) }));
  };

  const addPortfolioItem = async () => {
    if (!newPortfolioItem.url || !newPortfolioItem.title) { toast.error('Please provide URL and title'); return; }
    try {
      await axios.post(`${API}/creatives/portfolio`, newPortfolioItem, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Portfolio item added!');
      setNewPortfolioItem({ url: '', type: 'image', title: '', description: '' });
      fetchProfile();
    } catch (error) {
      toast.error('Failed to add portfolio item');
    }
  };

  const removePortfolioItem = async (itemId) => {
    try {
      await axios.delete(`${API}/creatives/portfolio/${itemId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Portfolio item removed');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const toggleFeatured = async (itemId) => {
    const item = portfolioItems.find(i => i.item_id === itemId);
    if (!item) return;
    const featuredCount = portfolioItems.filter(i => i.featured).length;
    if (!item.featured && featuredCount >= 5) { toast.error('Maximum 5 featured items allowed'); return; }
    setPortfolioItems(prev => prev.map(i => i.item_id === itemId ? { ...i, featured: !i.featured } : i));
    try {
      await axios.patch(`${API}/creatives/portfolio/${itemId}/featured`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      setPortfolioItems(prev => prev.map(i => i.item_id === itemId ? { ...i, featured: item.featured } : i));
      toast.error('Failed to update featured status');
    }
  };

  // Tags the photographer has created for themselves (used on a photo but not in the global list)
  const customTags = [...new Set(portfolioItems.flatMap(i => i.tags || []))]
    .filter(t => !GLOBAL_PORTFOLIO_TAGS.includes(t));
  const availableTags = [...GLOBAL_PORTFOLIO_TAGS, ...customTags].sort((a, b) => a.localeCompare(b));

  const setItemTags = async (itemId, tags) => {
    const prev = portfolioItems;
    setPortfolioItems(p => p.map(i => i.item_id === itemId ? { ...i, tags } : i));
    try {
      await axios.patch(`${API}/creatives/portfolio/${itemId}/tags`, { tags }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      setPortfolioItems(prev);
      toast.error('Failed to update tags');
    }
  };

  const toggleItemTag = (item, tag) => {
    const current = item.tags || [];
    const next = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    setItemTags(item.item_id, next);
  };

  const addCustomTag = (item) => {
    const t = newTagInput.trim();
    if (!t) return;
    if (!(item.tags || []).includes(t)) setItemTags(item.item_id, [...(item.tags || []), t]);
    setNewTagInput('');
  };

  const allSpecialties = [...specialtiesOptions.photography, ...specialtiesOptions.videography];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="text-[#a0a0a0] text-[13px]">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-[#e4e1db] sticky top-0 z-10">
        <div className="px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <Link
              to="/dashboard"
              className="w-8 h-8 rounded-full border border-[#e4e1db] flex items-center justify-center text-[#a0a0a0] hover:bg-[#f7f5f2] hover:text-[#0e0e0e] transition-colors"
            >
              <IconArrowLeft />
            </Link>
            <span className="font-display font-normal text-[20px] text-[#0e0e0e]">Edit Profile</span>
          </div>
          <div className="flex items-center gap-2.5">
            {profileId && (
              <Link
                to={`/photographers/${(user?.name || '').replace(/\s+/g, '')}_${profileId.replace('profile_', '')}`}
                target="_blank"
                className={ghostBtn}
              >
                <IconEye />
                <span className="hidden sm:inline">Preview Profile</span>
              </Link>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className={inkBtn}
              data-testid="save-profile-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-5 md:px-10 py-6 max-w-full space-y-5 pb-20">

        {/* Profile Picture */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Profile Picture</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">This appears on your public profile and in search results.</p>
          <div className="flex items-center gap-5">
            {/* Preview */}
            <div className="relative w-[100px] h-[100px] rounded-[10px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center flex-shrink-0 overflow-hidden">
              {profilePicture ? (
                <img
                  src={profilePicture.startsWith('/api') ? `${API.replace('/api', '')}${profilePicture}` : profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera className="w-8 h-8 text-[#c0bdb8]" />
              )}
              {uploadingPicture && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            {/* Upload */}
            <div>
              <label className="cursor-pointer" style={{ display: 'block', marginBottom: '8px' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                  disabled={uploadingPicture}
                  data-testid="profile-picture-input"
                />
                <span className={ghostBtn}>
                  <IconUpload />
                  {profilePicture ? 'Change Picture' : 'Upload Picture'}
                </span>
              </label>
              <p className="text-[11px] text-[#c0bdb8]">Square image recommended. At least 400×400px. Max 10MB.</p>
            </div>
          </div>
        </div>

        {/* Banner Image */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Banner Image</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">The full-width image that appears at the top of your profile page.</p>
          <div className="relative w-full h-[140px] rounded-[10px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center mb-3 overflow-hidden">
            {bannerImage ? (
              <img
                src={bannerImage.startsWith('/api') ? `${API.replace('/api', '')}${bannerImage}` : bannerImage}
                alt="Banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <Image className="w-8 h-8 text-[#c0bdb8]" />
            )}
            {uploadingBanner && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" disabled={uploadingBanner} />
            <span className={ghostBtn}>
              <IconUpload />
              {bannerImage ? 'Change Banner' : 'Upload Banner'}
            </span>
          </label>
          <p className="text-[11px] text-[#c0bdb8] mt-2">Wide image recommended (1200×400px or similar). Max 10MB.</p>
        </div>

        {/* Account Security */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Account Security</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Manage your login credentials.</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[14px] font-medium text-[#0e0e0e]">Password</p>
              <p className="text-[12px] text-[#a0a0a0] mt-0.5">Reset your account password via email</p>
            </div>
            <button
              onClick={handleRequestPasswordReset}
              disabled={requestingReset}
              className={ghostBtn}
              data-testid="reset-password-btn"
            >
              <KeyRound className="w-3 h-3" />
              {requestingReset ? 'Sending...' : 'Reset Password'}
            </button>
          </div>
        </div>

        {/* Billing - Business only */}
        {user?.role?.toLowerCase() === 'business' && (
          <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
            <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Payment Method</h2>
            <p className="text-[13px] text-[#a0a0a0] mb-6">Save a payment method to speed up checkout when booking photographers.</p>
            {billingStatus.has_payment_method ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[13px] font-medium text-[#16a34a] bg-[#f0fdf4] border border-[#bbf7d0] rounded-[10px] px-4 py-3">
                  <CheckCircle className="w-4 h-4" />
                  <span className="capitalize">{billingStatus.card_brand} ending in {billingStatus.card_last4}</span>
                </div>
                <button onClick={handleManageBilling} className={ghostBtn}>
                  <ExternalLink className="w-3 h-3" />
                  Manage Payment Method
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#f7f5f2] rounded-[10px] p-4">
                  <ul className="text-[13px] text-[#a0a0a0] space-y-1.5">
                    <li>• Skip entering card details at every checkout</li>
                    <li>• Payments are securely handled by Stripe</li>
                    <li>• Your card info is never stored on our servers</li>
                  </ul>
                </div>
                <button
                  onClick={handleSetupBilling}
                  disabled={settingUpBilling}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#635BFF] text-white text-[12px] font-medium hover:bg-[#5046e5] transition-colors disabled:opacity-50"
                >
                  <CreditCard className="w-3 h-3" />
                  {settingUpBilling ? 'Redirecting...' : 'Add Payment Method'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Stripe Connect - Creative only */}
        {user?.role?.toLowerCase() === 'creative' && (
          <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
            <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Payment Setup</h2>
            <p className="text-[13px] text-[#a0a0a0] mb-6">Connect your Stripe account to receive payments directly when clients approve your work.</p>

            {stripeStatus.connected && stripeStatus.payouts_enabled ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[13px] font-medium text-[#16a34a] bg-[#f0fdf4] border border-[#bbf7d0] rounded-[10px] px-4 py-3">
                  <CheckCircle className="w-4 h-4" />
                  Stripe Connected — Ready to receive payments!
                </div>
                <button onClick={handleOpenStripeDashboard} className={ghostBtn} data-testid="stripe-dashboard-btn">
                  <ExternalLink className="w-3 h-3" />
                  Open Stripe Dashboard
                </button>
              </div>
            ) : stripeStatus.connected && stripeStatus.onboarding_complete ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[13px] font-medium text-[#1d4ed8] bg-[#eff6ff] border border-[#bfdbfe] rounded-[10px] px-4 py-3">
                  <CheckCircle className="w-4 h-4" />
                  Stripe account verified — Payouts will be enabled shortly!
                </div>
                <button onClick={handleOpenStripeDashboard} className={ghostBtn} data-testid="stripe-dashboard-pending-btn">
                  <ExternalLink className="w-3 h-3" />
                  Open Stripe Dashboard
                </button>
              </div>
            ) : stripeStatus.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-[13px] font-medium text-[#92400e] bg-[#fffbeb] border border-[#fde68a] rounded-[10px] px-4 py-3">
                  <Clock className="w-4 h-4" />
                  Almost there! Complete your Stripe setup to receive payments.
                </div>
                <p className="text-[13px] text-[#a0a0a0]">You've created a Stripe account. Now finish the verification process to start receiving payouts.</p>
                <button
                  onClick={handleConnectStripe}
                  disabled={connectingStripe}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#635BFF] text-white text-[12px] font-medium hover:bg-[#5046e5] transition-colors disabled:opacity-50"
                  data-testid="complete-stripe-btn"
                >
                  <CreditCard className="w-3 h-3" />
                  {connectingStripe ? 'Redirecting...' : 'Complete Stripe Setup'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#f7f5f2] rounded-[10px] p-4">
                  <p className="text-[13px] font-semibold text-[#0e0e0e] mb-2">Why connect Stripe?</p>
                  <ul className="text-[13px] text-[#a0a0a0] space-y-1.5">
                    <li>• Get paid directly to your bank account</li>
                    <li>• Secure, instant payouts when work is approved</li>
                    <li>• Track all your earnings in one place</li>
                  </ul>
                </div>
                <button
                  onClick={handleConnectStripe}
                  disabled={connectingStripe}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#635BFF] text-white text-[12px] font-medium hover:bg-[#5046e5] transition-colors disabled:opacity-50"
                  data-testid="connect-stripe-btn"
                >
                  <CreditCard className="w-3 h-3" />
                  {connectingStripe ? 'Redirecting...' : 'Connect with Stripe'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Basic Information</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Tell clients who you are and what you do.</p>

          <div className="space-y-5">
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">
                Professional Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={profile.title}
                onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                placeholder="e.g. Professional Product Photographer"
                className={inputCls}
                data-testid="profile-title-input"
              />
            </div>

            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Bio</label>
              <Textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Tell clients about yourself and your work..."
                className={`${inputCls} min-h-[100px] resize-y`}
                rows={4}
                data-testid="profile-bio-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">
                  City <span className="text-red-500">*</span>
                </label>
                <Input
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  placeholder="e.g. Los Angeles"
                  className={inputCls}
                  data-testid="profile-city-input"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Full Address (optional)</label>
                <Input
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="e.g. Los Angeles, CA, USA"
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Pricing</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Set your rates so clients know what to expect.</p>

          <div className="space-y-5">
            {(profile.pricing_type === 'hourly' || profile.pricing_type === 'both') && (
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Hourly Rate ($)</label>
                <Input
                  type="number"
                  min="0"
                  value={profile.hourly_rate}
                  onChange={(e) => setProfile({ ...profile, hourly_rate: Number(e.target.value) })}
                  placeholder="100"
                  className={`${inputCls} w-40`}
                  data-testid="profile-rate-input"
                />
              </div>
            )}

            {(profile.pricing_type === 'project' || profile.pricing_type === 'both') && (
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Project Price Range ($)</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={profile.project_price_min}
                    onChange={(e) => setProfile({ ...profile, project_price_min: Number(e.target.value) })}
                    placeholder="Min"
                    className={`${inputCls} w-32`}
                  />
                  <span className="text-[#a0a0a0] text-[13px]">to</span>
                  <Input
                    type="number"
                    min="0"
                    value={profile.project_price_max}
                    onChange={(e) => setProfile({ ...profile, project_price_max: Number(e.target.value) })}
                    placeholder="Max"
                    className={`${inputCls} w-32`}
                  />
                </div>
                <p className="text-[11px] text-[#c0bdb8] mt-1.5">Average range for your typical projects</p>
              </div>
            )}
          </div>
        </div>

        {/* Specialties */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Specialties</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Select all the types of photography and videography you offer.</p>

          {/* Standard specialty chips */}
          <div className="flex gap-2 flex-wrap">
            {allSpecialties.map((specialty) => (
              <button
                key={specialty}
                type="button"
                onClick={() => handleSpecialtyToggle(specialty)}
                className={`px-4 py-[7px] rounded-full text-[12px] font-medium transition-all border ${
                  profile.specialties.includes(specialty)
                    ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                    : 'bg-white text-[#a0a0a0] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
                }`}
                data-testid={`specialty-${specialty.toLowerCase().replace(/ /g, '-')}`}
              >
                {specialty}
              </button>
            ))}
          </div>

          {/* Custom specialties */}
          {profile.specialties.filter(s => !allSpecialties.includes(s)).length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#e4e1db]">
              <p className="text-[12px] text-[#a0a0a0] mb-2">Custom specialties:</p>
              <div className="flex flex-wrap gap-2">
                {profile.specialties.filter(s => !allSpecialties.includes(s)).map((specialty) => (
                  <span
                    key={specialty}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0e0e0e] text-white text-[12px] font-medium"
                  >
                    {specialty}
                    <button
                      type="button"
                      onClick={() => handleSpecialtyToggle(specialty)}
                      className="ml-1 hover:opacity-70 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add custom specialty */}
          <div className="mt-5 pt-4 border-t border-[#e4e1db]">
            <p className="text-[12px] font-semibold text-[#0e0e0e] mb-2">Don't see your specialty?</p>
            <div className="flex gap-2">
              <Input
                placeholder="Enter your specialty..."
                value={customSpecialty}
                onChange={(e) => setCustomSpecialty(e.target.value)}
                className={`${inputCls} flex-1`}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomSpecialty())}
                data-testid="custom-specialty-input"
              />
              <button
                type="button"
                onClick={handleAddCustomSpecialty}
                className={ghostBtn}
                data-testid="add-custom-specialty-btn"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Service Packages */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <div className="flex items-start justify-between mb-1">
            <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px]">Packages</h2>
            {servicePackages.length < 3 && !showPackageForm && (
              <button
                onClick={() => setShowPackageForm(true)}
                className={ghostBtn}
                data-testid="add-package-btn"
              >
                <Plus className="w-3 h-3" />
                Add Package
              </button>
            )}
          </div>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Offer set packages so clients know exactly what they're getting.</p>

          {/* Existing packages grid */}
          {servicePackages.length > 0 && (
            <div className="grid md:grid-cols-2 gap-3.5 mb-5">
              {servicePackages.map((pkg) => (
                <div key={pkg.package_id} className="bg-[#f7f5f2] border border-[#e4e1db] rounded-[10px] p-[18px] relative">
                  {editingPackage?.package_id === pkg.package_id ? (
                    <div className="space-y-3">
                      <Input
                        value={editingPackage.name}
                        onChange={(e) => setEditingPackage({ ...editingPackage, name: e.target.value })}
                        placeholder="Package name"
                        className={inputCls}
                      />
                      <Textarea
                        value={editingPackage.description}
                        onChange={(e) => setEditingPackage({ ...editingPackage, description: e.target.value })}
                        placeholder="Description"
                        rows={2}
                        className={`${inputCls} resize-none`}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" value={editingPackage.price} onChange={(e) => setEditingPackage({ ...editingPackage, price: e.target.value })} placeholder="Price" className={inputCls} />
                        <Input type="number" value={editingPackage.duration_hours} onChange={(e) => setEditingPackage({ ...editingPackage, duration_hours: e.target.value })} placeholder="Hours" className={inputCls} />
                        <Input type="number" value={editingPackage.revisions_included} onChange={(e) => setEditingPackage({ ...editingPackage, revisions_included: e.target.value })} placeholder="Revisions" className={inputCls} />
                      </div>
                      <Textarea
                        value={editingPackage.deliverables}
                        onChange={(e) => setEditingPackage({ ...editingPackage, deliverables: e.target.value })}
                        placeholder="Deliverables included"
                        rows={2}
                        className={`${inputCls} resize-none`}
                      />
                      <div className="flex gap-2">
                        <button onClick={handleUpdatePackage} className={inkBtn}>Save</button>
                        <button onClick={() => setEditingPackage(null)} className={ghostBtn}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 absolute top-3.5 right-3.5">
                        <button
                          onClick={() => setEditingPackage(pkg)}
                          className="text-[11px] font-medium text-[#a0a0a0] bg-white border border-[#e4e1db] rounded-full px-2.5 py-1 hover:text-[#0e0e0e] hover:border-[#0e0e0e] transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePackage(pkg.package_id)}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-[#e4e1db] text-[#a0a0a0] hover:text-red-500 hover:border-red-200 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-[13px] font-semibold text-[#0e0e0e] mb-1 pr-20">{pkg.name}</p>
                      <p className="text-[20px] font-bold text-[#0e0e0e]">${pkg.price}</p>
                      <p className="text-[11px] text-[#c0bdb8] mt-1">
                        {pkg.duration_hours}h · {pkg.revisions_included} revisions · {pkg.deliverables}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {servicePackages.length === 0 && !showPackageForm && (
            <div className="text-center py-10 bg-[#f7f5f2] rounded-[12px]">
              <Package className="w-10 h-10 mx-auto mb-3 text-[#c0bdb8]" />
              <p className="text-[13px] text-[#a0a0a0]">No packages yet.</p>
              <p className="text-[12px] text-[#c0bdb8] mt-1">Create fixed-price packages to offer clients clear service options.</p>
            </div>
          )}

          {/* Add new package form */}
          {showPackageForm && (
            <div className="p-5 rounded-[12px] bg-[#f7f5f2] border border-[#e4e1db] space-y-4">
              <p className="text-[14px] font-semibold text-[#0e0e0e]">New Package</p>
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Package Name *</label>
                <Input
                  value={newPackage.name}
                  onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  placeholder="e.g. Half-Day Photo Shoot"
                  className={inputCls}
                  data-testid="package-name-input"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Description</label>
                <Textarea
                  value={newPackage.description}
                  onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  placeholder="Describe what's included in this package"
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Price ($) *</label>
                  <Input type="number" value={newPackage.price} onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })} placeholder="500" className={inputCls} data-testid="package-price-input" />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Duration (hrs)</label>
                  <Input type="number" value={newPackage.duration_hours} onChange={(e) => setNewPackage({ ...newPackage, duration_hours: e.target.value })} placeholder="4" className={inputCls} />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Revisions</label>
                  <Input type="number" value={newPackage.revisions_included} onChange={(e) => setNewPackage({ ...newPackage, revisions_included: e.target.value })} placeholder="2" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Deliverables *</label>
                <Textarea
                  value={newPackage.deliverables}
                  onChange={(e) => setNewPackage({ ...newPackage, deliverables: e.target.value })}
                  placeholder="e.g. 20 edited photos, 5 retouched portraits, all raw files"
                  rows={2}
                  className={`${inputCls} resize-none`}
                  data-testid="package-deliverables-input"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleCreatePackage} className={inkBtn} data-testid="save-package-btn">
                  <Plus className="w-3 h-3" />
                  Create Package
                </button>
                <button onClick={() => setShowPackageForm(false)} className={ghostBtn}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Portfolio */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Portfolio</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Upload your best work. These appear on your public profile portfolio tab. Add tags to each photo so clients can filter your work.</p>

          {/* Drag-and-drop upload area */}
          <label
            className={`flex flex-col items-center justify-center gap-2.5 w-full border-[1.5px] border-dashed rounded-[10px] p-8 cursor-pointer transition-all mb-6 ${
              uploadingPortfolio
                ? 'opacity-60 border-[#e4e1db] bg-[#f7f5f2]'
                : isPortfolioDragging
                  ? 'border-[#0e0e0e] bg-white'
                  : 'border-[#cac7c0] bg-[#f7f5f2] hover:border-[#0e0e0e] hover:bg-white'
            }`}
            onDrop={handlePortfolioDrop}
            onDragOver={handlePortfolioDragOver}
            onDragEnter={handlePortfolioDragOver}
            onDragLeave={handlePortfolioDragLeave}
          >
            <input
              type="file"
              accept="image/*,video/*"
              onChange={handlePortfolioUpload}
              className="hidden"
              disabled={uploadingPortfolio}
              data-testid="portfolio-file-input"
            />
            {uploadingPortfolio ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#a0a0a0]" />
            ) : (
              <IconFolder />
            )}
            <span className="text-[13px] font-medium text-[#5a5a5a]">
              {uploadingPortfolio ? 'Uploading...' : 'Drag photos or videos here, or click to upload'}
            </span>
            {!uploadingPortfolio && (
              <span className="text-[11px] text-[#c0bdb8]">Supports JPG, PNG, MP4. Max 50MB per file.</span>
            )}
          </label>

          {/* Add by URL */}
          <div className="p-5 rounded-[12px] bg-[#f7f5f2] border border-[#e4e1db] mb-6">
            <p className="text-[13px] font-semibold text-[#0e0e0e] mb-4">Or Add by URL</p>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Image / Video URL</label>
                  <Input
                    value={newPortfolioItem.url}
                    onChange={(e) => setNewPortfolioItem({ ...newPortfolioItem, url: e.target.value })}
                    placeholder="https://..."
                    className={inputCls}
                    data-testid="portfolio-url-input"
                  />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Type</label>
                  <Select
                    value={newPortfolioItem.type}
                    onValueChange={(v) => setNewPortfolioItem({ ...newPortfolioItem, type: v })}
                  >
                    <SelectTrigger className={inputCls}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Title *</label>
                <Input
                  value={newPortfolioItem.title}
                  onChange={(e) => setNewPortfolioItem({ ...newPortfolioItem, title: e.target.value })}
                  placeholder="e.g. Product Shoot for XYZ Brand"
                  className={inputCls}
                  data-testid="portfolio-title-input"
                />
              </div>
              <button
                onClick={addPortfolioItem}
                className={ghostBtn}
                data-testid="add-portfolio-btn"
              >
                <Plus className="w-3 h-3" />
                Add URL to Portfolio
              </button>
            </div>
          </div>

          {/* Portfolio grid */}
          {portfolioItems.length > 0 && (
            <p className="text-[11px] text-[#a0a0a0] flex items-center gap-1 mb-3">
              <Star className="w-3 h-3" />
              Star up to 5 items to feature them on your Overview tab
            </p>
          )}
          {portfolioItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {portfolioItems.map((item, idx) => {
                const isYouTube = item.url?.includes('youtube.com') || item.url?.includes('youtu.be');
                const isVimeo = item.url?.includes('vimeo.com');
                const isEmbedVideo = isYouTube || isVimeo;

                const getYouTubeEmbedUrl = (url) => {
                  const videoId = url.includes('youtu.be')
                    ? url.split('youtu.be/')[1]?.split('?')[0]
                    : url.split('v=')[1]?.split('&')[0];
                  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
                };

                const getVimeoEmbedUrl = (url) => {
                  const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                  return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
                };

                const getVideoUrl = () => {
                  if (isYouTube) return getYouTubeEmbedUrl(item.url);
                  if (isVimeo) return getVimeoEmbedUrl(item.url);
                  return item.url.startsWith('/api') ? `${API.replace('/api', '')}${item.url}` : item.url;
                };

                return (
                  <div key={item.item_id || idx} className="flex flex-col">
                  <div className="relative group rounded-[10px] overflow-hidden bg-[#f7f5f2] aspect-[4/3]">
                    {item.type === 'video' ? (
                      isEmbedVideo ? (
                        <iframe
                          src={getVideoUrl()}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={item.title}
                        />
                      ) : (
                        <video src={getVideoUrl()} className="w-full h-full object-cover" controls />
                      )
                    ) : (
                      <img
                        src={item.url.startsWith('/api') ? `${API.replace('/api', '')}${item.url}` : item.url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found'; }}
                      />
                    )}
                    {/* Star button */}
                    {item.item_id && (
                      <button
                        onClick={() => toggleFeatured(item.item_id)}
                        className="absolute top-2 left-2 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                        title={item.featured ? 'Remove from featured' : 'Add to featured (max 5)'}
                      >
                        <Star className={`w-4 h-4 ${item.featured ? 'fill-amber-400 text-amber-400' : 'text-white'}`} />
                      </button>
                    )}
                    {/* Hover overlay with delete */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => removePortfolioItem(item.item_id)}
                        className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    {/* Title bar */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-[13px] font-medium truncate">{item.title}</p>
                      <div className="flex items-center gap-1 text-white/70 text-[11px]">
                        {item.type === 'video' ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                        {item.type}
                        {item.uploaded && <span className="ml-1">· Uploaded</span>}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {item.item_id && (
                    <div className="mt-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(item.tags || []).map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleItemTag(item, tag)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0e0e0e] text-white text-[10px] font-medium hover:opacity-70 transition-opacity"
                          >
                            {tag}
                            <X className="w-2.5 h-2.5" />
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setTagMenuOpenId(tagMenuOpenId === item.item_id ? null : item.item_id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#e4e1db] text-[#5a5a5a] text-[10px] font-medium hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-colors"
                        >
                          <Plus className="w-2.5 h-2.5" />
                          Tag
                        </button>
                      </div>

                      {tagMenuOpenId === item.item_id && (
                        <div className="mt-1.5 p-2 rounded-[10px] border border-[#e4e1db] bg-white">
                          <div className="flex flex-wrap gap-1">
                            {availableTags.map(tag => {
                              const on = (item.tags || []).includes(tag);
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => toggleItemTag(item, tag)}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all ${
                                    on
                                      ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                                      : 'bg-white text-[#a0a0a0] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
                                  }`}
                                >
                                  {tag}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex gap-1 mt-2">
                            <input
                              value={newTagInput}
                              onChange={(e) => setNewTagInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag(item))}
                              placeholder="Create a tag..."
                              className="flex-1 min-w-0 text-[11px] px-2 py-1 rounded-full border border-[#e4e1db] focus:outline-none focus:border-[#0e0e0e]"
                            />
                            <button
                              type="button"
                              onClick={() => addCustomTag(item)}
                              className="px-2.5 py-1 rounded-full bg-[#0e0e0e] text-white text-[11px] font-medium shrink-0"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#f7f5f2] rounded-[12px]">
              <Camera className="w-10 h-10 mx-auto text-[#c0bdb8] mb-3" />
              <p className="text-[13px] text-[#a0a0a0]">No portfolio items yet</p>
              <p className="text-[12px] text-[#c0bdb8] mt-1">Upload your best work to attract clients</p>
            </div>
          )}
        </div>

        {/* Bottom save button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={inkBtn}
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditProfile;
